export interface LegacyCustomerIdentity {
  customer_id: string;
  email: string | null;
  phone: string | null;
}

export interface GraphContactIdentity {
  contact_id: string;
  primary_email: string | null;
  primary_phone: string | null;
}

export interface GraphProjectionSummary {
  contact_id: string;
  total_messages?: number | null;
  total_bookings?: number | null;
  last_active_at?: string | null;
}

export interface ChannelIdentifierSummary {
  contact_id: string;
  identifier_value: string;
}

export interface UnifiedContactAssociation {
  customer: LegacyCustomerIdentity | null;
  contact: GraphContactIdentity | null;
  projection: GraphProjectionSummary | null;
  channelIdentifiers: string[];
}

const normalizeIdentity = (value: string | null | undefined) =>
  value?.trim().toLowerCase().replace(/\s+/g, "") || null;

const projectionScore = (projection: GraphProjectionSummary | null) => {
  if (!projection) return Number.NEGATIVE_INFINITY;
  const lastActive = projection.last_active_at
    ? new Date(projection.last_active_at).getTime()
    : 0;
  return Number(projection.total_messages ?? 0) * 1_000_000 +
    Number(projection.total_bookings ?? 0) * 10_000 +
    Math.floor(lastActive / 1_000_000_000);
};

/**
 * Join the legacy CRM customer table to the canonical Customer Graph.
 *
 * A messaging contact may have no email or phone yet, so matching only the
 * contact's primary fields makes LINE/Instagram/Telegram contacts disappear.
 * Channel identifiers are therefore first-class join keys. If an email contact
 * and a LINE contact both map to the same booking customer, the graph contact
 * with the richest messaging/activity projection wins and the duplicate is
 * suppressed.
 */
export function associateUnifiedContacts(args: {
  customers: LegacyCustomerIdentity[];
  contacts: GraphContactIdentity[];
  projections: GraphProjectionSummary[];
  channelIdentifiers: ChannelIdentifierSummary[];
}): UnifiedContactAssociation[] {
  const customerByIdentifier = new Map<string, LegacyCustomerIdentity>();
  for (const customer of args.customers) {
    const email = normalizeIdentity(customer.email);
    const phone = normalizeIdentity(customer.phone);
    if (email) customerByIdentifier.set(email, customer);
    if (phone) customerByIdentifier.set(phone, customer);
  }

  const projectionByContact = new Map(
    args.projections.map((projection) => [projection.contact_id, projection]),
  );
  const identifiersByContact = new Map<string, string[]>();
  for (const identifier of args.channelIdentifiers) {
    const values = identifiersByContact.get(identifier.contact_id) ?? [];
    values.push(identifier.identifier_value);
    identifiersByContact.set(identifier.contact_id, values);
  }

  const candidatesByCustomer = new Map<
    string,
    Array<{
      contact: GraphContactIdentity;
      projection: GraphProjectionSummary | null;
      channelIdentifiers: string[];
    }>
  >();
  const graphOnly: UnifiedContactAssociation[] = [];

  for (const contact of args.contacts) {
    const channelIdentifiers = identifiersByContact.get(contact.contact_id) ?? [];
    const identifiers = [
      contact.primary_email,
      contact.primary_phone,
      ...channelIdentifiers,
    ];
    const customer = identifiers
      .map(normalizeIdentity)
      .filter((value): value is string => !!value)
      .map((value) => customerByIdentifier.get(value))
      .find((value): value is LegacyCustomerIdentity => !!value) ?? null;
    const projection = projectionByContact.get(contact.contact_id) ?? null;

    if (!customer) {
      graphOnly.push({ customer: null, contact, projection, channelIdentifiers });
      continue;
    }

    const candidates = candidatesByCustomer.get(customer.customer_id) ?? [];
    candidates.push({ contact, projection, channelIdentifiers });
    candidatesByCustomer.set(customer.customer_id, candidates);
  }

  const associated: UnifiedContactAssociation[] = args.customers.map((customer) => {
    const candidates = candidatesByCustomer.get(customer.customer_id) ?? [];
    candidates.sort((a, b) => projectionScore(b.projection) - projectionScore(a.projection));
    const best = candidates[0];
    return {
      customer,
      contact: best?.contact ?? null,
      projection: best?.projection ?? null,
      channelIdentifiers: candidates.flatMap((candidate) => candidate.channelIdentifiers),
    };
  });

  return [...associated, ...graphOnly];
}
